<template>
  <div
    class="add-to-bag"
    :id="`addtocart-${props.object.id}`"
    aria-label="Change Quantity"
  >
    <div v-if="expanded && props.object.is_parent" class="dropdown-options">
      <p class="dropdown-header">Select Size</p>
      <Dropdown
        v-if="subproductOptions.length > 0"
        v-model="selectedOption"
        :options="subproductOptions"
        optionDisabled="disabled"
        optionLabel="name"
        optionValue="key"
        name="add_item_id"
        placeholder="Select an option"
        required
      />
      <div v-else class="alert alert-warning">Sold out</div>
    </div>
    <div v-if="expanded && object.extras">
      <div class="extras">
        <div v-if="object.extras.eventforms" class="eventforms">
          <EventForm
            v-for="form of object.extras.eventforms"
            :product="object"
            :key="form"
            :form="form"
            :ref="(ref) => (eventforms[form] = ref)"
            @error="handleEventFormError"
            @change="button = 'reset'"
          >
            <span />
          </EventForm>
        </div>
        <div class="flex-row flex-space-between">
          <div key="voucher">
            <p v-if="message" class="alert alert-warning">
              {{ message }}
            </p>
            <div class="flex-row">
              <InputText
                placeholder="Voucher code"
                v-model="voucher"
                @input="handleVoucherCode"
              />
              <div class="flex-stretch block">
                <i
                  v-if="voucher && voucher === promos.couponCode"
                  class="fa fa-check-circle text-success"
                />
                <i v-else-if="voucher && !message" class="fa fa-sync fa-spin" />
              </div>
            </div>
          </div>
          <div class="flex-stretch flex-align-center" />
          <div class="flex-column flex-align-bottom flex-center">
            <Message v-if="error" class="m-2xs">{{ error }}</Message>
            <div class="flex-row">
              <Button
                v-if="error || sourceProblem"
                disabled
                class="btn btn-default"
              >
                Can't Add
              </Button>
              <Button
                v-else-if="!object.active"
                disabled
                class="btn btn-default"
              >
                Out of stock
              </Button>
              <Button v-else @click="handleCustomize" class="btn btn-default">
                <template v-if="voucher && voucher != promos.couponCode">
                  Verifying
                </template>
                <template v-else-if="button === 'added'">Added!</template>
                <template v-else>Add</template>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-row" v-else-if="isStatic">
      <em></em>
    </div>
    <div class="buttons flex-row flex-align-center flex-static" v-else>
      <router-link
        class="btn btn-link"
        :to="{ name: 'product-detail', params: { slug: object.slug } }"
        v-if="props.object.is_parent && !expanded"
      >
        <Button class="grey">Choose Size</Button>
      </router-link>
      <router-link
        class="btn btn-link"
        :to="{ name: 'product-detail', params: { slug: object.slug } }"
        v-if="object.extras && !expanded"
      >
        <Button class="grey"> More info... </Button>
      </router-link>
      <Button
        v-else-if="!primaryCategory || (price == 0 && itemSource === 'default')"
        class="grey"
        :disabled="true"
      >
        <span v-if="!primaryCategory"> Unavailable [1] </span>
        <span v-if="price == 0 && itemSource === 'default'">
          Unavailable [2]
        </span>
      </Button>
      <span v-else class="flex-row">
        <InputNumber
          v-if="
            (props.showQuantity || (cartItem && cartItem.quantity > 0)) &&
            object.stock > 0
          "
          @update:model-value="updateQuantity"
          :model-value="cartItem?.quantity || qty || 1"
          class="add-quantity"
          showButtons
          buttonLayout="horizontal"
          :min="0"
          :max="object.stock < 300 ? object.stock : 1000000"
          fluid
        >
          <template #incrementbuttonicon>
            <span class="pi pi-plus" />
          </template>
          <template #decrementbuttonicon>
            <span class="pi pi-minus" />
          </template>
        </InputNumber>

        <TimedButton
          v-if="!cartItem || props.showQuantity"
          data-adding-text="Adding..."
          data-added-text="Added!"
          data-failed-text="Failed!"
          type="button"
          @click.prevent="handleAddToCart"
          :unlock="releaseDate"
          :state="button"
          :disabled="!object.active || !productSlug || !allowedForNewSignups"
          class="grey"
          :aria-label="'Add to Cart'"
        >
          <span v-if="!object.active">Out of stock</span>
          <span v-else-if="productSlug">
            <template v-if="!allowedForNewSignups"> Can`t Add </template>
            <template v-else-if="error || sourceProblem"> Can't Add </template>
            <!-- <template v-else-if="added > 0">Added {{ added }}</template> -->
            <template v-else>Add</template>
          </span>
          <span v-else>Add</span>
        </TimedButton>
      </span>
      <WishlistAdd
        v-if="primaryCategory && showWishlist"
        :product="object.id"
        class="add-wishlist-btn flex-static"
      >
        <span />
      </WishlistAdd>
    </div>
  </div>
</template>

<script setup lang="ts">
import TimedButton from "@/components/shop/TimedButton.vue";
import WishlistAdd from "@/components/shop/WishlistAdd.vue";
import type { Category, Product } from "@/stores/defs/shop_defs";
import { useShopStore } from "@/stores/shop";
import { useUserStore } from "@/stores/user";
import { addItemsToCart } from "@/stores/utils/add_to_cart";
import copy from "@virgodev/bazaar/functions/copy";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import Message from "primevue/message";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

import { useCartStore } from "@/stores/cart";
import { useWindowStore } from "@/stores/window";

import { useJoinStore } from "@/stores/join";
import { usePromosStore } from "@/stores/promos";
import { cancelDebounce, debounce } from "@/utils/bouncer";
import type { CartItem } from "@paparazzi/types/paparazzi.api.serializers.base";
import InputText from "primevue/inputtext";
import EventForm from "./EventForm.vue";
// import BlinkTransition from "paparazzi/components/blink_transition";

const props = withDefaults(
  defineProps<{
    object: Product;
    quantity?: number;
    expanded?: boolean;
    placeholder?: number;
    showQuantity?: boolean;
    showWishlist?: boolean;
  }>(),
  {
    quantity: 1,
    expanded: false,
    placeholder: 1,
    showQuantity: false,
    showWishlist: true,
  },
);
const emit = defineEmits(["added", "update:quantity"]);

const win = useWindowStore();
const user = useUserStore();
const cart = useCartStore();
const shop = useShopStore();
const promos = usePromosStore();
const join = useJoinStore();

const message = ref("");
const voucher = ref(cart.object.coupon_code || promos.couponCode);
const qty = ref(props.quantity);
const selectedOption = ref("");
const button = ref("reset");
const error: Ref<Error | string | null> = ref(null);
const editing = ref(false);
const eventforms: Ref<{ [index: string]: any }> = ref({});
const sizeOrder = [
  "X-Small",
  "XS",
  "S",
  "Small",
  "M",
  "Medium",
  "L",
  "Large",
  "XL",
  "X-Large",
  "2XL",
  "2X-Large",
  "3XL",
  "3X-Large",
  "4XL",
  "4X-Large",
];

let timeout = -1;
let __couponCode = -1;

const allowedForNewSignups = computed(() => {
  let excluded: number[] = [];
  if (join.kit) {
    const names = ["test", "Zi Bracelets 2024"];
    for (const name of names) {
      const cat = shop.categories.find((c) => c.name === name);
      if (cat) {
        excluded.push(cat.id);
        excluded = excluded.concat(shop.descendents[cat.id].map((c) => c.id));
      }
    }
  }
  return !props.object.categories.some((c) => excluded.includes(c));
});

const myQuantity = computed(() => {
  let retval = qty.value;
  // if (win.ctrlDown) {
  //   retval = qty.value * 50;
  // }
  // if (win.shiftDown) {
  //   retval = qty.value * 5;
  // }
  return retval;
});
const price = computed(() => {
  if (user.groups.indexOf("wholesale") > -1) {
    return props.object.prices.wholesale;
  }
  return props.object.prices.null;
});
const productSlug = computed(() => {
  let slug = props.object.slug;
  if (props.object.is_parent) {
    slug = selectedOption.value;
  }
  return slug;
});
const isStatic = computed(() => {
  return shop.staticCategoriesId.indexOf(props.object.category) > -1;
});
const primaryCategory = computed(() => {
  let category = props.object.category;
  return shop.allowedCategories.find((c: Category) => c.id === category);
});
const itemSource = computed(() => {
  if (primaryCategory.value) {
    return primaryCategory.value.inventory_source || "default";
  }
  return "default";
});
const cartSource = computed(() => cart.inventorySource);
const sourceProblem = computed(() => {
  return itemSource.value != cartSource.value && cart.items.length > 0;
});
const cartItem = computed(() => {
  return cart.items.find((item: CartItem) => {
    return item.product?.slug === productSlug.value;
  });
});
const added: Ref<number> = computed(() => {
  if (cartItem.value) {
    return cartItem.value.quantity;
  }
  return 0;
});
const subproducts = computed<Record<string, Product>>(() => {
  return shop.objects.products
    .filter((p) => {
      return p.parent?.id === props.object.id;
    })
    .reduce(
      (a, b) => {
        a[b.id] = b;
        return a;
      },
      {} as Record<string, Product>,
    );
});
const subproductOptions = computed<{ name: string; key: string }[]>(() => {
  return (props.object.subproducts ?? [])
    .slice()
    .sort((a, b) => {
      return sizeOrder.indexOf(a.option) - sizeOrder.indexOf(b.option);
    })
    .map((o: any) => ({
      key: o.slug,
      name:
        `${o.option}` +
        (subproducts.value[o.child]?.active ? "" : " - Not Available"),
      disabled: !subproducts.value[o.child]?.active,
    }));
});

const releaseDate = computed<string | undefined>(() => {
  let min: Date | undefined;
  if (props.object.release_dates) {
    for (const key in props.object.release_dates) {
      if (user.groups.includes(key)) {
        const date = new Date(props.object.release_dates[key]);
        if (!min || min > date) {
          min = date;
        }
      }
    }
  }
  if (!min && props.object.release_date) {
    min = new Date(props.object.release_date);
  }
  if (min) {
    return min.toISOString();
  }
  return;
});

//   computed: {
//     dbObject() {
//       return this.$store.getters.indexedProducts.find((p) => p.id === this.object.id);
//     },
//     cart() { return this.$store.state.shop.cart; },
//     user() { return this.$store.state.auth.user; },
//   },
//   watch: {
//     myQuantity() {
//       this.$emit("update:quantity", this.myQuantity);
//     },
//   },
//   methods: {

watch(
  () => props.quantity,
  () => {
    qty.value = props.quantity;
  },
);

watch(cartItem, () => {
  if (cartItem.value) {
    qty.value = cartItem.value.quantity;
  } else {
    qty.value = 0;
  }
});

function updateQuantity(changedTo: number) {
  qty.value = changedTo;
  if (cartItem.value) {
    const original = cartItem.value.quantity;
    cartItem.value.quantity = qty.value;
    if (qty.value <= 0) {
      if (cart.object.items) {
        const index = cart.object.items.indexOf(cartItem.value);
        if (index > -1) {
          cart.object.items.splice(index, 1);
        }
      }
    }

    // cart item might have been removed
    if (cartItem.value) {
      const max = shop.getMax(cartItem.value?.product?.id);
      if (cartItem.value.quantity > max) {
        cartItem.value.quantity = max;
      }
    }

    if (!cartItem.value || original !== cartItem.value.quantity) {
      cart.object.changed = new Date().toISOString();
    }
  }

  emit("update:quantity", changedTo);
}

async function handleVoucherCode() {
  clearTimeout(__couponCode);
  message.value = "";
  if (voucher.value) {
    if (await debounce("test-voucher")) {
      if (!(await promos.getCouponPromo(voucher.value))) {
        message.value = "Invalid Voucher Code";
        promos.couponPromo = undefined;
      }
      editing.value = false;
    }
  } else {
    cancelDebounce("test-voucher");
    editing.value = false;
    promos.couponCode = "";
    promos.couponPromo = undefined;
  }
}

async function handleAddToCart() {
  if (sourceProblem.value) {
    if (itemSource.value === "default") {
      console.warn(`Clear your cart before you add normal shop items`);
      //   toaster.add({
      //     severity: "warn",
      //     detail: `Clear your cart before you add normal shop items`,
      //   });
    } else {
      console.warn(
        `Clear your cart before you add exclusive items from ${itemSource.value}`,
      );
      //   toaster.add({
      //     severity: "warn",
      //     detail: `Clear your cart before you add exclusive items from ${itemSource.value}`,
      //   });
    }
  } else {
    clearTimeout(timeout);
    timeout = window.setTimeout(async () => {
      button.value = "adding";

      // verify product
      let slug = productSlug.value;
      if (slug) {
        const response = await addItemsToCart([
          {
            slug,
            quantity: myQuantity.value,
          },
        ]);
        emit("added", response);
        button.value = "added";
      } else {
        button.value = "reset";
      }
    }, 11);

    return false;
  }
}

async function handleCustomize() {
  let valid = true;
  const extras: { [index: string]: any } = {};

  for (const key in eventforms.value) {
    const el = eventforms.value[key] as typeof EventForm;
    const data = el.isValid();
    if (data === null) {
      valid = false;
    } else {
      extras[el.data.object.form_name] = copy(data);
    }
  }

  if (valid) {
    // remove existing voucher item
    if (promos.couponPromo) {
      cart.object.items = cart.object.items.filter(
        (item) => item.promotion !== promos.couponPromo,
      );
    }

    const response = await addItemsToCart([
      {
        slug: productSlug.value,
        quantity: myQuantity.value,
        extras: { eventforms: extras },
        promotion: promos.couponPromo,
      },
    ]);
    if (response.ok) {
      voucher.value = "";
      button.value = "added";

      for (const key in eventforms.value) {
        eventforms.value[key].clearForm();
      }
    } else {
      error.value = Object.values(response.errors).join(", ");
    }
  } else {
    button.value = "reset";
  }
}

function handleEventFormError(ex: Error) {
  error.value = ex;
}
</script>

<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.flex-row {
  gap: var(--2XS, 8px);
}
.flex-row .form-control {
  width: 30px;
  display: flex;
}
.extras {
  border-radius: 5px;
  padding: 0;
  max-width: 550px;
}
.extra-buttons {
  padding: 5px;
  text-align: right;
}
.grey {
  flex: 0 0 auto;
}

.add-to-bag {
  white-space: nowrap;
  text-overflow: ellipsis;
  .dropdown-options {
    margin-bottom: var(--XL, 56px);
  }
}

.add-to-bag :deep(.p-inputnumber-button) {
  background-color: #fff;
  color: #000;
  padding: 14px 0;
}

.add-to-bag :deep(.p-button.p-button-icon-only) {
  width: 7px !important;
}

/* .add-to-bag :deep(.p-inputtext) {
  border: none;
} */

p.dropdown-header {
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 300;
  margin: var(--3XS, 4px) 0;
  text-align: left;
}

:deep(svg.p-icon) {
  width: 11px;
}
.qty :deep(input) {
  padding: 0;
  text-align: center;
  max-width: 4em;
}
.add-quantity {
  padding: 0 2px;
  & :deep(input) {
    padding: 0;
    width: 3em;
    text-align: center;
  }
  & :deep(input.p-inputtext) {
    border: none;
  }
}

span.pi {
  font-size: 6px;
}
</style>
